import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dialog, DialogContent, FormControl, IconButton, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { defaultFunction } from '../../utilities/utilities'
import { ID_CAMERA_MRZ } from '../../../variables'
import { parseMrz } from '../InfraredReader/parseMrz'
import { coerceInfraredData, isConsistentInfraredData } from '../InfraredReader/InfraredDataValidator'
import InfraredScanErrorDialog from '../InfraredReader/InfraredScanErrorDialog'
import '../IDDialog/IDDialog.sass'
import './MRZDialog.sass'

const MRZ_WORKER_PATH = '/mrz-reader/tesseract/worker.min.js'
const MRZ_CORE_PATH = '/mrz-reader/tesseract/'
const MRZ_LANG_PATH = '/mrz-reader/model/'
const MRZ_SCAN_INTERVAL_MS = 1000
const MRZ_SCAN_REGION = { left: 0.08, top: 0.62, width: 0.84, height: 0.28 }

function installCameraConstraint(deviceId) {
  const mediaDevices = navigator.mediaDevices
  if (!deviceId || typeof mediaDevices?.getUserMedia !== 'function') return () => {}
  const original = mediaDevices.getUserMedia.bind(mediaDevices)
  mediaDevices.getUserMedia = (constraints = {}) => {
    const video = constraints.video && typeof constraints.video === 'object' ? constraints.video : {}
    return original({
      ...constraints,
      audio: false,
      video: {
        ...video,
        deviceId: { exact: deviceId },
      },
    })
  }
  return () => {
    mediaDevices.getUserMedia = original
  }
}

function MRZDialog(props) {
  const {
    open = false,
    onClose = defaultFunction,
    onChangDataByID = defaultFunction,
    cameras = [],
    isCostaRica = false,
    loading = false,
  } = props

  const { t } = useTranslation()
  const readerRef = useRef(null)
  const [containerNode, setContainerNode] = useState(null)
  const [camera, setCamera] = useState('')
  const [localCameras, setLocalCameras] = useState([])
  const [cameraBlocked, setCameraBlocked] = useState(false)
  const [scanErrorOpen, setScanErrorOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const canEnumerateCameras = typeof navigator.mediaDevices?.enumerateDevices === 'function'

  const loadingRef = useRef(loading)
  const isCostaRicaRef = useRef(isCostaRica)
  const onChangDataByIDRef = useRef(onChangDataByID)
  const onCloseRef = useRef(onClose)
  const scanErrorOpenRef = useRef(false)
  const scanningPausedRef = useRef(false)

  useEffect(() => { loadingRef.current = loading }, [loading])
  useEffect(() => { isCostaRicaRef.current = isCostaRica }, [isCostaRica])
  useEffect(() => { onChangDataByIDRef.current = onChangDataByID }, [onChangDataByID])
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  const handleResult = useCallback((result) => {
    if (loadingRef.current || scanErrorOpenRef.current) return
    const parsed = parseMrz(result?.raw)
    if (!parsed) {
      scanningPausedRef.current = true
      scanErrorOpenRef.current = true
      readerRef.current?.pauseScanning?.()
      setScanErrorOpen(true)
      return
    }
    const options = parsed.documentType ? { documentType: parsed.documentType } : {}
    const coerced = coerceInfraredData(parsed.data)
    if (!isConsistentInfraredData(coerced, { isCostaRica: isCostaRicaRef.current, ...options })) {
      scanningPausedRef.current = true
      scanErrorOpenRef.current = true
      readerRef.current?.pauseScanning?.()
      setScanErrorOpen(true)
      return
    }
    scanningPausedRef.current = true
    readerRef.current?.pauseScanning?.()
    onChangDataByIDRef.current(coerced)
    onCloseRef.current()
  }, [])

  const handleCameraChange = (event) => {
    localStorage.setItem(ID_CAMERA_MRZ, event.target.value)
    setCamera(event.target.value)
  }

  useEffect(() => {
    if (open) return undefined
    setCameraBlocked(false)
    setScanErrorOpen(false)
    setCapturing(false)
    scanErrorOpenRef.current = false
    scanningPausedRef.current = false
    return undefined
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    if (!canEnumerateCameras) {
      setCameraBlocked(true)
      return undefined
    }

    const enumerateLocalCameras = () => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput')
          if (videoDevices.length > 0) setLocalCameras(videoDevices)
        })
        .catch(() => {})
    }

    enumerateLocalCameras()
    navigator.mediaDevices.addEventListener('devicechange', enumerateLocalCameras)
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateLocalCameras)
    }
  }, [open, canEnumerateCameras])

  useEffect(() => {
    if (cameras.length > 0) setLocalCameras(cameras)
  }, [cameras])

  useEffect(() => {
    if (!open || !canEnumerateCameras) return undefined
    if (localCameras.length > 0 && localCameras.some(item => item.label)) return undefined

    const timer = setTimeout(() => {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(d => d.kind === 'videoinput')
          if (videoDevices.length > 0) setLocalCameras(videoDevices)
        })
        .catch(() => {})
    }, 1500)

    return () => clearTimeout(timer)
  }, [open, canEnumerateCameras, localCameras])

  useEffect(() => {
    if (!open || localCameras.length === 0) return
    const stored = localStorage.getItem(ID_CAMERA_MRZ)
    const isValid = localCameras.some(item => item.deviceId === stored)
    setCamera(isValid ? stored : localCameras[0].deviceId)
  }, [open, localCameras])

  useEffect(() => {
    if (!open || !containerNode || !camera) return undefined

    let cancelled = false
    const restoreGetUserMedia = installCameraConstraint(camera)

    import('web-mrz-reader')
      .then(({ initMRZReader }) => {
        if (cancelled) return
        readerRef.current = initMRZReader({
          container: containerNode,
          autoScan: false,
          drawBoundingBoxes: false,
          scanRegion: MRZ_SCAN_REGION,
          workerPath: MRZ_WORKER_PATH,
          corePath: MRZ_CORE_PATH,
          langPath: MRZ_LANG_PATH,
          onResult: handleResult,
          onError: (error) => {
            if (typeof error === 'string' && error.startsWith('Error accessing the camera')) {
              setCameraBlocked(true)
            }
          },
        })
      })
      .catch(() => {
        if (!cancelled) setCameraBlocked(true)
      })

    const intervalId = setInterval(() => {
      if (cancelled || scanningPausedRef.current || scanErrorOpenRef.current || loadingRef.current) return
      readerRef.current?.capture?.()
    }, MRZ_SCAN_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(intervalId)
      restoreGetUserMedia()
      readerRef.current?.stop()
      readerRef.current = null
    }
  }, [open, containerNode, camera, handleResult])

  const closeScanError = () => {
    scanErrorOpenRef.current = false
    scanningPausedRef.current = false
    setScanErrorOpen(false)
  }

  const captureManually = async () => {
    if (capturing || scanningPausedRef.current || scanErrorOpenRef.current || loadingRef.current) return
    setCapturing(true)
    try {
      await readerRef.current?.capture?.()
    } finally {
      setCapturing(false)
    }
  }

  return (
    <>
      <Dialog
        className="visitor-dialog"
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          <Stack gap={4}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <h1>{t('Scan MRZ')}</h1>
              <IconButton aria-label="close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
            {cameraBlocked ? (
              <p style={{ color: 'var(--error, #d32f2f)', textAlign: 'center' }}>
                {t('Camera access requires a secure connection (HTTPS). Please contact your administrator.')}
              </p>
            ) : (
              <Stack gap={2} alignItems="center">
                <FormControl fullWidth>
                  <InputLabel id="select-mrz-camera">{t('Camera')}</InputLabel>
                  <Select
                    labelId="select-mrz-camera"
                    value={camera}
                    label={t('Camera')}
                    size="small"
                    onChange={handleCameraChange}
                  >
                    {localCameras.map((cam) => (
                      <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div className="webcam-wrapper-ID mrz-webcam">
                  <div ref={setContainerNode} className="mrz-webcam-stage" />
                  <hr className="hr-animation" />
                </div>
                <h2>{t('Place the MRZ in front of the camera.')}</h2>
                <Stack gap={2} direction="row" justifyContent="center">
                  <Button variant="outlined" onClick={captureManually} disabled={capturing || loading}>
                    {t('Capture Manually')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
      <InfraredScanErrorDialog
        open={scanErrorOpen}
        onClose={closeScanError}
      />
    </>
  )
}

const mapStateToProps = state => {
  return {
    isCostaRica: !!state.profile.enabled_costa_rica,
    loading: state.backdrop.loading,
    cameras: state.utilities?.cameras || [],
  }
}

export default connect(mapStateToProps)(MRZDialog)
